import { useEffect, useMemo, useState } from 'react'
import { useMetaDataContext } from '../state-management/contexts/MetaDataContext'
import useAlternateISIN from './useAlternateISIN'
import { ElitePowerRankLeaderboardResponseType, WaterfallSections } from '../types/mutual-fund-detail.types'
import { useQuery } from '@tanstack/react-query'
import { fetchPowerRankLeaderboard } from '../repository/MutualFundDetail'
import { EventNames } from '../types/misc.types'
import { trackEvent } from '../utils/appBridge'
import { opacityElementByAttribute } from '../utils/utils'

interface Fund {
  isin: string
  fundName: string
  powerRank: number
}

export const useViewLeaderboard = () => {
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [peerData, setPeerData] = useState<Fund[]>([])

  const { data: metaData } = useMetaDataContext()

  const { alternateISIN } = useAlternateISIN(WaterfallSections.ELITE)

  const fundCategory = useMemo(() => {
    return metaData?.fundDetails?.category ?? ''
  }, [metaData])

  const { data: returnScoresData } = useQuery<any, Error>({
    queryKey: ['powerRankLeaderboard', fundCategory],
    queryFn: () => {
      return fetchPowerRankLeaderboard(fundCategory)
    },
    enabled: !!alternateISIN,
    refetchOnWindowFocus: false,
    retry: 1
  })

  const openBottomSheet = () => {
    setIsSheetOpen(true)
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        eventId: 'View Leaderboard',
        ScreenName: 'MF_L2_ELITE',
        fundName: alternateISIN,
        screenType: 'MF_L2'
      }
    })
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '1')
  }

  const closeBottomSheet = () => {
    setIsSheetOpen(false)
    opacityElementByAttribute('data-rsbs-backdrop', 'true', '0')
  }

  useEffect(() => {
    if (returnScoresData) {
      const dataCopy = returnScoresData?.data as ElitePowerRankLeaderboardResponseType
      setPeerData(() => dataCopy?.rankList as Fund[])
    }
  }, [returnScoresData])

  const getRank = () => {
    return peerData?.filter((peerDatum) => peerDatum?.isin === alternateISIN)?.[0]?.powerRank ?? 0
  }

  return { openBottomSheet, closeBottomSheet, peerData, isSheetOpen, getRank, alternateISIN, fundCategory }
}
